import {BaseSyntheticEvent} from 'react';
import {BreadcrumbsDTO} from '../models/dto/BreadcrumbsDTO';
import {SortType} from '../models/SortType';
import {SortOrder} from '../models/SortOrder';
import {getSortTypeByKey} from './DtoUtils';
import {config} from '../init/config';
import {ONE_MEGABYTE_BITS} from '../init/constants';

// Generic Helper utils
export const executeAction = (e: BaseSyntheticEvent, action: () => void) => {
    e.stopPropagation();
    action();
};

export const isTrue = (value: string): boolean => {
    return (/true/i).test(value);
};

export const isFalse = (value: string): boolean => {
    return !isTrue(value);
};

export const validateContainsWhitespace = (value: string): boolean => {
    if (isNullOrEmpty(value)) {
        return true;
    }

    return (value.indexOf(' ') >= 0);
};

export const SPECIAL_CHARACTERS = '@#$%&?`\'\\"/';

export const SPECIAL_END_CHARACTERS = '\\/.';

export const validateContainsSpecialCharacter = (value: string | null): boolean => {
    return (!value) || (/[@#$%&?`'\\"/]/).test(value);
};

export const validateEndWithSpecialCharacter = (value: string | null): boolean => {
    return (!value) || (/[\\/.]$/).test(value);
};

export const isNullOrEmpty = (value: string | null): boolean => {
    return (!value) || (/^\s*$/).test(value);
};

//UI specific Helper utils. The utils are specially designed to be used in forms, dialogs etc.
//Example: the usage of isValidText can be applied with it's implementation but the usage of the function
//facilitates a lot the validation check of a text field(user input). The same function is not appropriate for usage
//into functions of general purpose as isValidText means nothing but the syntax of check for undefined and not null is meaningful.
export const isValidText = (value: string | null | undefined): boolean => {
    return ((value !== undefined) && (!isNullOrEmpty(value))) as boolean;
};

export const isValidNumber = (value: number | null | undefined): boolean => {
    return ((value !== undefined) && value) as boolean;
};

export const isValidDate = (value: Date | null | undefined): boolean => {
    return ((value !== undefined) && value) as boolean;
};

export const isValidBoolean = (value: boolean | null | undefined): boolean => {
    return ((value !== undefined) && (value !== null)) as boolean;
};

export const isValidStringArray = (value: string[] | null | undefined): boolean => {
    return ((value !== undefined) && value && value.length > 0) as boolean;
};

export const validateThumbnailImage = (image: File | null): boolean => {
    return ((image !== undefined) && image && (image.size !== undefined) && image.size && (image.size > 0) && (image.size <= (config.DLS_THUMBNAIL_MAX_SIZE * ONE_MEGABYTE_BITS))) as boolean;
};

export const pathToResource = (breadcrumbs: BreadcrumbsDTO[]): string => {
    let path = breadcrumbs.map(obj => obj.labelPathPart);
    return ('/'.concat(path.join('/')));
};

export const getPath = (pathParam: string, nameParam: string): string => {
    let path = '/';
    if (!isNullOrEmpty(pathParam)) {
        path = pathParam;
    }

    if (isNullOrEmpty(nameParam)) {
        nameParam = '';
    }

    if (path !== '/') {
        path = path + '/';
    }

    return path + nameParam;
};

export const getDateStart = (date: Date): Date => {
    let effectiveDate = new Date(date);
    effectiveDate.setHours(0, 0, 0, 0);

    return effectiveDate;
};

export const getEffectiveDate = (): Date => {
    return getDateStart(new Date());
};

export const convertToStringArray = (value: string[] | null | undefined): string[] => {
    if ((value !== undefined) && value && (value.length > 0)) {
        return value.toString().replace('[', '').replace(']', '').split(',')
            .filter(item => (!isNullOrEmpty(item)))
            .map(item => item.trim());
    }

    return [];
};

export const getEnumKeyByValue = (enumerated: {
    [s: string]: unknown;
} | ArrayLike<unknown>, value: string): string => {
    return Object.keys(enumerated)[Object.values(enumerated).indexOf(value as typeof enumerated)] ?? '';
};

export const getDefaultSortOrderByType = (sortType: string): SortOrder => {
    switch (getSortTypeByKey(sortType)) {
        case SortType.UD:
            return SortOrder.DESC;
        case SortType.AZ:
        default:
            return SortOrder.ASC;
    }
};

export const getLongestLength = (data: string[]): number => {
    if ((data === undefined) || (!data) || (data.length === 0)) {
        return 0;
    }

    return data.reduce(function (a, b) {
        return a.length > b.length ? a : b;
    }).length;
};

export const getFontSize = (data: string[], correction = 0): number => {
    if ((data === undefined) || (!data) || (data.length === 0)) {
        return 16;
    }

    let stringLength = getLongestLength(data);
    let min = 0;
    let max = 14 + correction;
    if ((stringLength >= min) && (stringLength < max)) {
        return 16;
    }

    min = max;
    max = max + 2;
    if ((stringLength >= min) && (stringLength < max)) {
        return 14;
    }

    min = max;
    max = max + 2;
    if ((stringLength >= min) && (stringLength < max)) {
        return 12;
    }

    return 10;
};
